import { completeEscrowDeposit, fetchEscrowAccountInfo, releaseEscrowDeposit } from '../api/escrow';
import { getAuthToken, getBidderId } from './user';
import { buildUrl } from '../api/helpers';
import { createSelector } from 'reselect';
import { getCatalog } from './catalog';
import { getDeployment } from './config';
import { getLocale } from './intl';
import type { GlobalState } from '../rootReducer';
import isEmpty from 'lodash/isEmpty';
import { isUiMobile } from './browser';
import variables from '../../theme/styles.scss';

export const STATUS_CREATING = 'CreationPending';
export const STATUS_CREATED = 'CreationConfirmed';
export const STATUS_RELEASING = 'ReleasePending';
export const STATUS_RELEASED = 'ReleaseConfirmed';
export const STATUS_LOCKED = 'Locked';

/* Action Types */
export const ESCROW_ACCOUNTS_REQUEST = 'la/user/escrow/ESCROW_ACCOUNT_REQUEST';
export const ESCROW_ACCOUNTS_FAIL = 'la/user/escrow/ESCROW_ACCOUNT_FAIL';
export const ESCROW_ACCOUNTS_SUCCESS = 'la/user/escrow/ESCROW_ACCOUNT_SUCCESS';
export const RELEASE_ESCROW_REQUEST = 'la/user/escrow/RELEASE_ESCROW_REQUEST';
export const RELEASE_ESCROW_FAIL = 'la/user/escrow/RELEASE_ESCROW_FAIL';
export const RELEASE_ESCROW_SUCCESS = 'la/user/escrow/RELEASE_ESCROW_SUCCESS';

// reducer
const DEFAULT_STATE = {
    error: null,
    escrowAccount: null,
    fetchingEscrowAccount: false,
    releasingEscrow: false,
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case RELEASE_ESCROW_FAIL:
            return {
                ...state,
                releasingEscrow: false,
            };
        case RELEASE_ESCROW_SUCCESS:
            return {
                ...state,
                escrowAccount: { ...state.escrowAccount, status: STATUS_RELEASING },
                releasingEscrow: false,
            };
        case RELEASE_ESCROW_REQUEST:
            return {
                ...state,
                releasingEscrow: true,
            };
        case ESCROW_ACCOUNTS_FAIL:
            return {
                ...state,
                error: action.payload,
                fetchingEscrowAccount: false,
            };
        case ESCROW_ACCOUNTS_SUCCESS:
            return {
                ...state,
                escrowAccount: action.payload,
                fetchingEscrowAccount: false,
            };
        case ESCROW_ACCOUNTS_REQUEST:
            return {
                ...state,
                error: null,
                fetchingEscrowAccount: true,
            };
        default:
            return state;
    }
}

const getInstantDepositWidget = () => (typeof window === 'undefined' ? false : window.InstantDepositWidget);

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.escrow;
export const isReleasingEscrow = createSelector(stateSelector, state => state.releasingEscrow);
export const isFetchingEscrow = createSelector(stateSelector, state => state.fetchingEscrowAccount);
export const getEscrowAccount = createSelector(stateSelector, state => state.escrowAccount);

/* Action Creators */
export const releaseEscrow = (depositId: any) => async (dispatch: Function, getState: Function) => {
    const state = getState();

    const isMobile = Boolean(isUiMobile(state));
    const releasingEscrow = isReleasingEscrow(state);

    if (isMobile || releasingEscrow) {
        return;
    }

    try {
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        await dispatch({ type: RELEASE_ESCROW_REQUEST });
        const { success, error } = await releaseEscrowDeposit({ authToken, deployment, depositId });
        if (success) {
            await dispatch({ type: RELEASE_ESCROW_SUCCESS });
        } else {
            await dispatch({ error, type: RELEASE_ESCROW_FAIL });
        }
    } catch (e) {
        await dispatch({ error: e, type: RELEASE_ESCROW_FAIL });
    }
};
export const fetchEscrowAccount = () => async (dispatch: Function, getState: Function) => {
    const state = getState();

    const isMobile = Boolean(isUiMobile(state));
    if (isMobile) {
        return;
    }

    try {
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        await dispatch({ type: ESCROW_ACCOUNTS_REQUEST });
        const { error, payload, success } = await fetchEscrowAccountInfo({ authToken, bidderId, deployment });

        if (success) {
            await dispatch({ payload, type: ESCROW_ACCOUNTS_SUCCESS });
        } else {
            await dispatch({ payload: error, type: ESCROW_ACCOUNTS_FAIL });
        }
    } catch (e) {
        await dispatch({ payload: e, type: ESCROW_ACCOUNTS_FAIL });
    }
};
export const openBiddableModal = (catalogId: number, callback: Function) => async (
    dispatch: Function,
    getState: Function,
) => {
    const state = getState();

    const { escrowSupported } = { escrowSupported: true, ...getCatalog(state, catalogId) };
    const { status } = getEscrowAccount(state) || {};
    const deployment = getDeployment(state);
    const isMobile = Boolean(isUiMobile(state));
    const hasEscrow = status && status !== STATUS_RELEASED;

    if (!escrowSupported || hasEscrow || isMobile) {
        return;
    }

    const InstantDepositWidget = getInstantDepositWidget();
    if (!InstantDepositWidget) {
        return;
        // throw new Error('Instant approval is turned on but no biddable integration was found.');
    }

    const isProd = deployment.includes('prod');
    const authToken = getAuthToken(state);
    const depositUrl = buildUrl('<ESCROW-API>', deployment, 'deposit');
    const widget = new InstantDepositWidget({
        accountSettingsUrl: '/dashboard/profile?tab=instant-approval-deposit',
        brandingCssOptions: {
            brandColor: variables.blue100,
            buttonBorderRadius: '2px',
        },
        callback: (error = null, result = {}) => {
            if (error && !isEmpty(error)) {
                console.error('an error occurred:', error);
            } else {
                const { result: payload } = result;
                dispatch(completeDeposit(payload));
            }

            callback && callback(result);
        },
        data: { catalogId, token: authToken },
        depositUrl: depositUrl,
        expectedNetworkId: isProd ? '1' : '4', // 1 = Mainnet, 4 = Rinkeby (dev network)
        // deployment === prod so we can test with a lower amount of real ETH in preprod
        forcedUSDValue: deployment === 'prod' ? 1000 : undefined,
        isGlobalApproval: true,
        language: getLocale(state) || 'en',
        platformName: 'LiveAuctioneers',
    });
    widget.openPopup();
};

const completeDeposit = ({ depositId }) => async (dispatch, getState) => {
    const state = getState();
    const authToken = getAuthToken(state);
    const deployment = getDeployment(state);
    try {
        await completeEscrowDeposit({ authToken, deployment, depositId });
        dispatch(fetchEscrowAccount());
    } catch (e) {
        console.error('an error occurred:', e);
    }
};
